/**
 * Google Tag Manager loader (replaces `gatsby-plugin-google-tagmanager`).
 *
 * The plugin injected the official snippet inline and synchronous in <head>,
 * so gtm.js (~180 KB) and the gtag library it pulls (~190 KB) were fetched,
 * parsed and executed while React was still hydrating — ~390 ms of main-thread
 * work landing exactly in the window where the first taps happen. Ablation
 * measured the home's INP at 208 ms with GTM and 72 ms without it, and the lab
 * TBT at 460 ms with GTM and 0 ms without (see
 * `ai-cms/reports/inp-home-regression-diagnosis-2026-09-04.md` §3 and
 * `ai-cms/reports/perf/deploy-verification-2026-09-14.md` §3).
 *
 * Everything observable stays the same — same container id, same `dataLayer`,
 * same `{'gtm.start', event:'gtm.js'}` bootstrap push, same
 * `gatsby-route-change` push on client-side navigation, same <noscript>
 * iframe (now emitted from gatsby-ssr.js). Only the timing changes: the
 * container is requested on the first user interaction, or at the first idle
 * slot from 2000 ms after `onInitialClientRender`, whichever comes first.
 *
 * What we lose: a visit that bounces before interacting and before the idle
 * slot is not seen by GTM. What we gain: the whole container is off the
 * hydration critical path.
 *
 * The consent-mode defaults and `window.dataLayer = window.dataLayer || []`
 * stay inline and synchronous in <head> (gatsby-ssr.js) so every `dataLayer`
 * push made before this loader runs is simply queued and replayed by gtm.js —
 * which is how the container has always consumed them.
 */

/**
 * Container id per locale. Keep in sync with the copy in `gatsby-ssr.js`
 * (which needs it at build time for the <noscript> fallback and cannot import
 * a TypeScript module).
 */
export const GTM_IDS: Record<string, string> = {
  es: "GTM-PL83KZL",
  fr: "GTM-PL83KZL",
  it: "GTM-PL83KZL",
  axa_wl_es: "GTM-PL83KZL",
};

export const GTM_ID: string | undefined =
  GTM_IDS[process.env.GATSBY_APP_LOCALE as string];

/** Same default as the plugin's `routeChangeEventName` option. */
export const GTM_ROUTE_CHANGE_EVENT = "gatsby-route-change";

/**
 * Google tag gateway: nginx on the landing VM proxies `/q7tz/` to
 * `gtm-pl83kzl.fps.goog`, so gtm.js and the Google tags' hits are first-party
 * and ad blockers stop cutting the measurement. gtm.js picks the path up and
 * sends its hits through it. Requires the `/q7tz/` location on every vhost
 * (`ai-cms/scripts/one-off/nginx-google-tag-gateway-2026-10-20.sh`) BEFORE
 * this ships — without it GTM does not load at all. Rollback: put back
 * "https://www.googletagmanager.com/gtm.js?id=".
 */
const GTM_SRC = "/q7tz/gtm.js?id=";

/**
 * `passive` so the listeners can never delay a scroll or a tap, `capture` so a
 * component that stops propagation cannot hide the interaction from us.
 */
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
] as const;

const LISTENER_OPTIONS: AddEventListenerOptions = {
  passive: true,
  once: true,
  capture: true,
};

/**
 * How long we wait before loading GTM on our own initiative.
 *
 * `requestIdleCallback`'s own `timeout` is a CEILING, not a floor: measured on
 * this build the home's first idle slot arrives ~20 ms after
 * `onInitialClientRender` (317 ms into the load), so `rIC(load, {timeout:2000})`
 * would still drop the container's ~390 ms of CPU right in the middle of the
 * load — images, lazy chunks and the first taps. 2000 ms is therefore used as a
 * floor: the container arrives once the visit has stopped being a bounce, and
 * an interaction before that loads it immediately anyway.
 */
const IDLE_DELAY_MS = 2000;

/**
 * Ceiling for the idle slot we ask for after the delay. Short, so a busy main
 * thread can postpone GTM by a few frames but never starve it.
 */
const IDLE_TIMEOUT_MS = 500;

let scheduled = false;
let loaded = false;

const getDataLayer = (): any[] => {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
};

/** Safe to call at any time: the push is queued until gtm.js consumes it. */
export const pushToDataLayer = (payload: Record<string, unknown>): void => {
  if (typeof window === "undefined") return;
  getDataLayer().push(payload);
};

const removeInteractionListeners = (): void => {
  INTERACTION_EVENTS.forEach((event) =>
    window.removeEventListener(event, loadGtm, LISTENER_OPTIONS)
  );
};

/** Inject gtm.js exactly once. Idempotent — every trigger calls it. */
function loadGtm(): void {
  if (loaded || typeof document === "undefined" || !GTM_ID) return;
  loaded = true;
  removeInteractionListeners();

  // The bootstrap push the official snippet makes before appending the script.
  // GTM reads `gtm.start` to compute container load time and `event:'gtm.js'`
  // is what fires the "Initialization"/"All Pages" triggers.
  getDataLayer().push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTM_SRC}${GTM_ID}`;
  script.dataset.thirdParty = "gtm";
  document.head.appendChild(script);
}

/**
 * Arm the triggers. Call once, from `onInitialClientRender` — by then React has
 * committed the first render, so the idle callback can only be served after
 * hydration is done.
 */
export const scheduleGtm = (): void => {
  if (typeof window === "undefined" || !GTM_ID || scheduled) return;
  scheduled = true;

  INTERACTION_EVENTS.forEach((event) =>
    window.addEventListener(event, loadGtm, LISTENER_OPTIONS)
  );

  window.setTimeout(() => {
    const idle = (window as any).requestIdleCallback;
    if (typeof idle === "function") {
      idle(loadGtm, { timeout: IDLE_TIMEOUT_MS });
    } else {
      // Safari < 16.4 and friends: no idle detection, load straight away.
      loadGtm();
    }
  }, IDLE_DELAY_MS);
};

/**
 * Client-side navigation event. Identical to what the plugin pushed from its
 * own `onRouteUpdate`, 50 ms delay included — the delay is there so
 * `document.title` has already been updated by Helmet when the GA4 page_view
 * tag reads it.
 */
export const pushRouteChange = (): void => {
  if (typeof window === "undefined" || !GTM_ID) return;
  window.setTimeout(() => {
    pushToDataLayer({ event: GTM_ROUTE_CHANGE_EVENT });
  }, 50);
};
